import {Component, ContentChild, OnInit, inject, input, signal, TemplateRef} from '@angular/core';
import {Apollo} from 'apollo-angular';
import {NgTemplateOutlet} from '@angular/common';
import {map} from 'rxjs/operators';

import {BROWSE_PORTAL} from '../../queries-signiant';
import {Portal, PortalEntry} from '../../types-signiant';
import {DateComponent} from '../../date/date.component';
import {FileSizePipe} from '../../pipes/file-size-pipe';

/** One folder on the way from the portal's root to the folder on screen. */
export interface BrowsePlace {
  name: string;
  path: string;
  folderId: string | null;
}

/**
 * Browses a Signiant portal's files by navigation, as the Aspera share browser does: one folder is on screen at a
 * time, opening a folder shows its contents, and the path above lets you go back up or to any folder on it.
 *
 * Shared by both portal types on purpose: what is on the portal is the same question either way — an acquisition portal
 * offers files to import, a delivery portal shows whether what we sent actually arrived. Only the per-row action differs,
 * and that is projected in by the parent (`<ng-template let-entry>` … `</ng-template>`).
 *
 * **One folder is fetched per navigation.** The portal REST API costs one request per directory, so listing a whole
 * tree inside one request overruns the resolver's 30 s budget (thales-medianet has 21 top-level directories).
 */
@Component({
  selector: 'app-portal-browser',
  templateUrl: './portal-browser.component.html',
  imports: [DateComponent, FileSizePipe, NgTemplateOutlet],
})
export class PortalBrowserComponent implements OnInit {
  portalId = input.required<string>();
  /** Header of the action column; no action template means no column. */
  actionHeader = input('');

  /** Projected per-row action, receiving the entry as `$implicit`. */
  @ContentChild(TemplateRef) actionTemplate?: TemplateRef<{$implicit: PortalEntry}>;

  /** The folders from the portal's root to the folder on screen; empty is the root itself. */
  places = signal<BrowsePlace[]>([]);
  entries = signal<PortalEntry[]>([]);
  loading = signal(false);
  /** Signed out and empty are different answers and read differently. */
  noSession = signal(false);
  error = signal<string | null>(null);

  private apollo = inject(Apollo);
  /** Only the latest navigation may write the list: a slow answer for an earlier folder is dropped. */
  private latest = 0;

  /**
   * Loaded once, from the input. The portal detail page reads its own id from the route *snapshot*, so a different
   * portal means a rebuilt page.
   */
  ngOnInit(): void {
    this.load();
  }

  /** The folder on screen, or null at the portal's root. */
  current(): BrowsePlace | null {
    const places = this.places();
    return places.length ? places[places.length - 1] : null;
  }

  /** Show the contents of a folder. Files are not navigable. */
  open(entry: PortalEntry): void {
    if (!entry.isDirectory) {
      return;
    }
    this.places.update(places => [...places, {name: entry.name, path: entry.path, folderId: entry.folderId}]);
    this.load();
  }

  /** One folder up, or the root from the root. */
  up(): void {
    this.goTo(this.places().length - 1);
  }

  /** The folder at ``depth`` on the path: 0 is the portal's root, the last is the folder on screen. */
  goTo(depth: number): void {
    this.places.update(places => places.slice(0, Math.max(0, depth)));
    this.load();
  }

  reload(): void {
    this.load();
  }

  private load(): void {
    const id = ++this.latest;
    const place = this.current();
    this.loading.set(true);
    this.error.set(null);
    this.fetch(place?.path ?? null, place?.folderId ?? null).subscribe({
      next: (entries) => {
        if (id !== this.latest) {
          return;
        }
        // null (not empty) means the API could not list at all: no session.
        this.noSession.set(entries == null);
        this.entries.set(entries ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        if (id !== this.latest) {
          return;
        }
        this.error.set(err?.message ?? String(err));
        this.loading.set(false);
      },
    });
  }

  /** One directory. Emits null when the portal could not be listed at all. */
  private fetch(path: string | null, folderId: string | null) {
    return this.apollo.use('signiant')
      .query<{portal: Portal}>({
        query: BROWSE_PORTAL,
        variables: {portalId: this.portalId(), path: path || null, folderId: folderId || null},
        fetchPolicy: 'network-only',
      })
      .pipe(map(({data}) => data?.portal?.browse ?? null));
  }
}
